import { Divider } from "@mui/material";
import { Grid } from "@mui-wrapper";
import { FormContainer } from "@components/forms/form-container";
import { formatPlanName, formatSubjectType } from "@utils/data-display";
import { PlanLimitForm } from "@components/forms/plan-limit-form";
import { useAppNavigation } from "@router/use-app-navigation";
import { ProductDropdown } from "@pages/create-plan/create-plan-components/product-dropdown";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { validatePlanCreation } from "@utils/create-plan-utils";
import { CreatePlanDateSection } from "@pages/create-plan/create-plan-components/create-plan-date-section";
import {
  plansCreationStateSelector,
  setComments,
} from "@store/plan-creation/plan-creation-slice";
import { CompanyProfileTab, ProjectProfileTab } from "@router/route-params";
import { shouldShowProductLimits } from "@utils/product/product-utils";
import { Alert, NotificationDialog, TextField, Text } from "@flat-ui";
import { PlanCreationModeProps } from "@custom-types/base-types";
import { PlanCreationInfoAlert } from "@pages/create-plan/create-plan-components/plan-creation-info-alert";
import { PlanAppSettingsSection } from "@pages/create-plan/create-plan-components/plan-app-settings-section";
import { MAX_USUAL_SUBSCRIPTION_YEARS } from "@utils/plan-duration";
import { useShouldConfirmLongDuration } from "@utils/hooks/plan/use-long-duration-confirmation";
import { useState } from "react";
import {
  copiedPlanSceneSeatAssigneesSelector,
  isEndingCopiedPlanSelector,
  sceneSeatsToCreateSelector,
} from "@store/plan-creation/plan-creation-selectors";
import { SCENE_SEATS_DISPLAY_NAME } from "@components/plans-table/scene-licenses";

interface Props extends PlanCreationModeProps {
  /** Callback to show the summary page */
  setShouldShowSummary: () => void;
}

/**
 * Renders the "Create Plan" form used for creating and managing company and project plans.
 * This form includes fields for entering plan details, and validating input.
 */
export function CreatePlanForm({
  creationMode,
  setShouldShowSummary,
}: Props): React.JSX.Element | null {
  const { navigateToProfilePage } = useAppNavigation();

  const {
    allLimits,
    comments,
    planSubject: { planSubjectId, planSubjectType, planSubjectName },
    selectedProduct,
    tokensPerYear,
    copiedPlan,
    creationDates: { startDate, endDate, durationType, durationValue },
  } = useAppSelector(plansCreationStateSelector);
  const sceneSeatsToCreate = useAppSelector(sceneSeatsToCreateSelector);
  const shouldEndCopiedPlan = useAppSelector(isEndingCopiedPlanSelector);
  const sceneSeatAssignees = useAppSelector(
    copiedPlanSceneSeatAssigneesSelector
  );

  // The copy only comes with one seat per active user, so not every member may keep their seat
  const shouldWarnAboutLostSceneSeats =
    selectedProduct?.hasSceneSeats === true &&
    sceneSeatAssignees.length > sceneSeatsToCreate;

  const shouldConfirmLongDuration = useShouldConfirmLongDuration({
    creationMode,
  });
  const [isLongDurationDialogOpen, setIsLongDurationDialogOpen] =
    useState(false);

  const dispatch = useAppDispatch();

  if (planSubjectId === null || planSubjectType === null) {
    return null;
  }

  const shouldShowLimits =
    selectedProduct !== null
      ? shouldShowProductLimits(selectedProduct, planSubjectType)
      : false;

  /**
   * Holds the validation error message, or `null` if the form data is valid.
   * If not `null`, the "Create" button is disabled and the error message is displayed to the user
   */
  const validationErrorMessage: string | null = validatePlanCreation({
    subjectType: planSubjectType,
    selectedProduct,
    comments,
    startDate,
    endDate,
    durationType,
    durationValue,
    shouldShowLimits,
    allLimits,
    tokensPerYear,
  });

  return (
    <FormContainer
      handleCreateClicked={() => {
        if (shouldConfirmLongDuration) {
          setIsLongDurationDialogOpen(true);
          return;
        }
        setShouldShowSummary();
      }}
      handleCancelClicked={() => {
        if (planSubjectType === "COMPANY" || planSubjectType === "DEVICE") {
          navigateToProfilePage({
            subjectType: planSubjectType,
            subjectId: planSubjectId,
            tab: CompanyProfileTab.plans,
          });
        } else if (planSubjectType === "PROJECT") {
          navigateToProfilePage({
            subjectType: planSubjectType,
            subjectId: planSubjectId,
            tab: ProjectProfileTab.plans,
          });
        }
      }}
      shouldCreateButtonBeDisabled={validationErrorMessage !== null}
      createButtonTooltip={validationErrorMessage}
    >
      <>
        {copiedPlan ? (
          <Alert
            variant="info"
            title={`Copy of plan "${formatPlanName(copiedPlan.plan)}"`}
            description={
              shouldEndCopiedPlan
                ? "The copied plan ends when the copy starts."
                : "The copied plan stays unchanged and keeps running until its end date."
            }
            sx={{ mb: 2.5 }}
          />
        ) : (
          <PlanCreationInfoAlert
            creationMode={creationMode}
            subjectType={planSubjectType}
          />
        )}

        <Text variant="heading20">Plan Details</Text>

        <Grid sx={{ flexDirection: "column" }}>
          {/* plan subject name */}
          <Grid size={{ md: 6 }} sx={{ mb: 2.5, mx: 0 }}>
            <TextField
              label={formatSubjectType(planSubjectType)}
              variant="outlined"
              text={planSubjectName || ""}
              disabled
              fullWidth
              controlStyle={{ mt: 2 }}
            />
          </Grid>

          <Grid size={{ md: 6 }} sx={{ mb: 2.5, mx: 0 }}>
            <Divider />
          </Grid>

          {/* product type */}
          <Grid size={{ md: 6 }} sx={{ mb: 2.5, mx: 0 }}>
            <ProductDropdown
              subjectType={planSubjectType}
              creationMode={creationMode}
            />

            <PlanAppSettingsSection />
          </Grid>

          <Grid size={{ md: 6 }} sx={{ mb: 2.5, mx: 0 }}>
            {shouldShowLimits && (
              <Divider>
                {allLimits.some((limit) => limit.isRequired)
                  ? "Limits"
                  : "Limits (select at least one)"}
              </Divider>
            )}
          </Grid>

          {shouldShowLimits && (
            <Grid size={{ md: 6 }} sx={{ mb: 2.5, mx: 0 }}>
              <PlanLimitForm />
            </Grid>
          )}

          {shouldWarnAboutLostSceneSeats && (
            <Grid size={{ md: 6 }} sx={{ mb: 2.5, mx: 0 }}>
              <Alert
                variant="warning"
                title={`Some ${SCENE_SEATS_DISPLAY_NAME} assignments will be lost`}
                description={`${sceneSeatAssignees.length} members hold a SCENE seat of the copied plan, but the copy only comes with ${sceneSeatsToCreate}, as the number of active users is now lower. Please consider increasing the limit of active users.`}
              />
            </Grid>
          )}

          <Grid size={{ md: 6 }} sx={{ mb: 2.5, mx: 0 }}>
            <Divider />
          </Grid>

          <Grid
            container
            sx={{ mb: 2.5, mx: 0, justifyContent: "space-between" }}
          >
            <CreatePlanDateSection creationMode={creationMode} />
          </Grid>

          <Grid size={{ md: 6 }} sx={{ mb: 2.5, mx: 0 }}>
            <Divider />
          </Grid>

          {/* comments */}
          <Grid size={{ md: 6 }}>
            <TextField
              label="Comments"
              multiline
              // minRows={4}
              text={comments}
              controlStyle={{ mt: 2 }}
              onTextChanged={(text: string) => {
                dispatch(setComments(text));
              }}
              variant="outlined"
              fullWidth
              tag="required"
            />
          </Grid>
        </Grid>

        <NotificationDialog
          open={isLongDurationDialogOpen}
          notificationType="warning"
          title="Check the plan duration"
          confirmText="Continue"
          onCancel={() => setIsLongDurationDialogOpen(false)}
          onConfirm={() => {
            setIsLongDurationDialogOpen(false);
            setShouldShowSummary();
          }}
        >
          <Text variant="bodyL">
            This plan runs for more than {MAX_USUAL_SUBSCRIPTION_YEARS} years.
            Subscriptions are normally sold for one or three years, so please
            double check the duration before continuing.
          </Text>
        </NotificationDialog>
      </>
    </FormContainer>
  );
}
